<template>
  <div>
    <div class="box box-info">
      <form>
        <div class="p-fluid p-formgrid p-grid">
          <div class="p-field p-col-12 p-md-4">
            <label for="itemGroups" style="font-size: 1.4rem">Item Category</label>
            <Dropdown
              v-model="selectedGroup"
              :options="itemGroups"
              optionLabel="name"
              placeholder="Select Category"
              class="dropdown-custom"
              @change="filterItemsByGroup"
            />
          </div>
          
          <div class="p-field p-col-12 p-md-4">
            <label for="items" style="font-size: 1.4rem">Items</label>
            <MultiSelect 
              v-model="selectedItems" 
              :options="filteredItems"
              optionLabel="name" 
              placeholder="Select Items"
              :showCheckbox="true" 
              :filter="true"
              @filter="searchItems"
              class="multiselect-custom"
              :class="'large-font'"
            >
              <template #option="slotProps">
                <div class="p-multiselect-item-option">
                  <span>{{ slotProps.option.name }}</span>
                </div>
              </template>
            </MultiSelect>
          </div>

          <div class="p-field p-col-12 p-md-2 p-mt-5">
            <Button 
              @click.prevent="searchMultiItemChartData"
              class="p-button-raised" 
              label="Generate Report" 
              icon="pi pi-search"
            />
          </div>
        </div>
      </form>
    </div>
    
    <div class="chart-container">
      <div class="p-grid">
        <div class="p-col-12 p-md-6 chart-wrapper">
          <h4>Sales Data</h4>
          <div class="chart-subcontainer">
            <canvas id="sales-bar-chart"></canvas>
          </div>
          <div class="chart-subcontainer">
            <canvas id="sales-line-chart"></canvas>
          </div>
        </div>

        <div class="p-col-12 p-md-6 chart-wrapper">
          <h4>Purchase Data</h4>
          <div class="chart-subcontainer">
            <canvas id="purchase-bar-chart"></canvas>
          </div>
          <div class="chart-subcontainer">
            <canvas id="purchase-line-chart"></canvas>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import MultiSelect from 'primevue/multiselect'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'

const generateColor = () => {
  const hue = Math.floor(Math.random() * 360)
  return {
    bar: `hsla(${hue}, 70%, 50%, 0.7)`,
    line: `hsla(${hue}, 70%, 30%, 1)`
  }
}
export default {
  name: 'MultiItemChart',
  data() {
    return {
      selectedItems: [],
      selectedGroup: null,
      filteredItems: [],
      itemGroups: [],
      allItems: [],
      charts: {
        salesBar: null,
        salesLine: null,
        purchaseBar: null,
        purchaseLine: null
      },
      loading: false
    }
  },
  async mounted() {
    await this.$store.dispatch('loadItemMasterList')
    this.allItems = [...this.itemMasterListByItemType]
    const groups = new Set()
    this.allItems.forEach(item => {
      if (item.groupName) {
        groups.add(item.groupName)
      }
    })
    this.itemGroups = Array.from(groups).map(group => ({
      name: group,
      id: group.toLowerCase().replace(/\s+/g, '-')
    }))
    this.itemGroups.unshift({ name: 'All Items', id: 'all-items' })
    this.selectedGroup = this.itemGroups[0]
    this.filteredItems = this.allItems
  },
  computed: {
    itemMasterListByItemType() {
      return this.$store.state.itemMaster.itemMasterLists
    }
  },
  methods: {
    filterItemsByGroup() {
      if (!this.selectedGroup || this.selectedGroup.id === 'all-items') {
        this.filteredItems = this.allItems
      } else {
        this.filteredItems = this.allItems.filter(
          item => item.groupName === this.selectedGroup.name
        )
      }
      this.selectedItems = []
    },
    async searchMultiItemChartData() {
      if (this.selectedItems.length === 0) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Please select at least one item!',
          life: 3000
        })
        return
      }
      this.cleanupCharts()
      this.resetCanvases()

      this.loading = true
      try {
        const itemIds = this.selectedItems.map(item => item.id)
        const response = await this.$store.dispatch(
          'loadMultiItemSalesMonthlyReport',
          itemIds
        )
        console.log(response)
        this.createCharts(response)
      } catch (error) {
        console.error('Error loading charts:', error)
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load chart data',
          life: 3000
        })
      } finally {
        this.loading = false
      }
    },
    cleanupCharts() {
      Object.entries(this.charts).forEach(([key, chart]) => {
        if (chart) {
          chart.destroy()
          this.charts[key] = null
        }
      })
      if (typeof window.gc === 'function') window.gc()
    },
    resetCanvases() {
      const canvases = [
        'sales-bar-chart',
        'sales-line-chart',
        'purchase-bar-chart',
        'purchase-line-chart'
      ]
      canvases.forEach(id => {
        const canvas = document.getElementById(id)
        if (canvas) {
          canvas.width = canvas.offsetWidth
          canvas.height = canvas.offsetHeight
          const ctx = canvas.getContext('2d')
          ctx.clearRect(0, 0, canvas.width, canvas.height)
        }
      })
    },
    createCharts(chartData) {
      const colors = this.selectedItems.map(() => generateColor())
      this.charts.salesBar = new Chart(document.getElementById('sales-bar-chart'), {
        type: 'bar',
        data: this.getChartData(chartData, 'salesData', colors, 'bar'),
        options: this.getChartOptions('Monthly Sales (Bar)')
      })
      this.charts.salesLine = new Chart(document.getElementById('sales-line-chart'), {
        type: 'line',
        data: this.getChartData(chartData, 'salesData', colors, 'line'),
        options: this.getChartOptions('Monthly Sales (Line)')
      })
      this.charts.purchaseBar = new Chart(document.getElementById('purchase-bar-chart'), {
        type: 'bar',
        data: this.getChartData(chartData, 'purchaseData', colors, 'bar'),
        options: this.getChartOptions('Monthly Purchase (Bar)')
      })
      this.charts.purchaseLine = new Chart(document.getElementById('purchase-line-chart'), {
        type: 'line',
        data: this.getChartData(chartData, 'purchaseData', colors, 'line'),
        options: this.getChartOptions('Monthly Purchase (Line)')
      })
    },
    getChartData(chartData, dataType, colors, chartType) {
      return {
        labels: chartData.labels,
        datasets: this.selectedItems.map((item, index) => ({
          label: item.name,
          data: chartData[dataType][item.id] || [],
          backgroundColor: chartType === 'bar' ? colors[index].bar : undefined,
          borderColor: colors[index].line,
          borderWidth: 2,
          fill: false,
          tension: 0.4
        }))
      }
    },
    getChartOptions(title) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 20,
            bottom: 20,
            left: 15,
            right: 15
          }
        },
        plugins: {
          title: {
            display: true,
            text: title,
            font: { size: 18 }
          },
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 12,
              padding: 20
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: '#f0f0f0' },
            ticks: {
              callback: value => Number(value).toLocaleString(),
              font: { size: 12 }
            }
          },
          x: {
            grid: { display: false },
            ticks: { font: { size: 12 } }
          }
        }
      }
    },
    searchItems(event) {
      const filterText = event.filter.toLowerCase() || ''
      if (this.selectedGroup && this.selectedGroup.id !== 'all-items') {
        this.filteredItems = this.allItems.filter(item =>
          item.groupName === this.selectedGroup.name &&
          item.name.toLowerCase().includes(filterText)
        )
      } else {
        this.filteredItems = this.allItems.filter(item =>
          item.name.toLowerCase().includes(filterText)
        )
      }
    }
  },
  components: {
    MultiSelect,
    Button,
    Dropdown
  },
  beforeDestroy() {
    this.cleanupCharts()
  }
}
</script>

<style scoped>
.multiselect-custom, .dropdown-custom {
  font-size: 1.4rem;
  min-height: 35px;
}

.item-name {
  font-size: 1.3rem;
  margin-left: 8px;
}

::v-deep .p-multiselect-label, 
::v-deep .p-dropdown-label {
  font-size: 1.3rem !important;
  padding: 0.75rem !important;
}

::v-deep .p-multiselect-item,
::v-deep .p-dropdown-item {
  font-size: 1.3rem !important;
  padding: 0.75rem 1.25rem !important;
}

.chart-container {
  margin-top: 2rem;
  padding: 1rem;
}

.chart-wrapper {
  margin-bottom: 2rem;
}

.chart-subcontainer {
  height: 400px;
  margin: 1rem 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  padding: 1.5rem;
}

h4 {
  color: #2c3e50;
  margin: 1.5rem 0;
  font-size: 1.8rem;
  border-bottom: 2px solid #eee;
  padding-bottom: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// salesAnalyisItemWiseMonthly.vue?37036da0